import { useRef, useState } from "react";

import * as service from "../services/albumsService";

const ALLOWED_VISIBILITY = ["public", "unlisted", "private", "group"];

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const isPositiveInteger = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0;
};

const normalizeGroupIds = (values) => {
  if (!Array.isArray(values)) return [];

  return [...new Set(values.map(Number).filter(isPositiveInteger))];
};

const normalizeVisibility = (value) =>
  String(value || "private")
    .trim()
    .toLowerCase();

const getResponseData = (response) => response?.data;

const isSuccessful = (data) => data != null && data.success !== false;

export const useCreatorAlbums = (username) => {
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState(null);
  const [albumDetails, setAlbumDetails] = useState(null);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pendingRequests = useRef(0);

  const startLoading = () => {
    pendingRequests.current += 1;
    setLoading(true);
  };

  const stopLoading = () => {
    pendingRequests.current = Math.max(0, pendingRequests.current - 1);

    if (pendingRequests.current === 0) {
      setLoading(false);
    }
  };

  const handleError = (operation, requestError, fallback) => {
    console.error(`${operation} failed:`, requestError);

    setError(getErrorMessage(requestError, fallback));

    return false;
  };

  const refreshAlbums = async () => {
    const response = await service.getCreatorAlbums(username);
    const data = getResponseData(response);

    if (!Array.isArray(data)) {
      throw new Error("Invalid albums response.");
    }

    setAlbums(data);

    return data;
  };

  const refreshAlbumsAfterMutation = async () => {
    try {
      await refreshAlbums();
    } catch (refreshError) {
      console.error("Unable to refresh albums after mutation:", refreshError);

      setError(
        "Changes were saved, but the album list could not be refreshed.",
      );
    }
  };

  const getCreatorAlbums = async () => {
    if (!username) {
      setError("Creator username is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      return await refreshAlbums();
    } catch (requestError) {
      return handleError(
        "getCreatorAlbums",
        requestError,
        "Failed to load albums.",
      );
    } finally {
      stopLoading();
    }
  };

  const getCreatorAlbum = async (albumId) => {
    if (!username || !isPositiveInteger(albumId)) {
      setError("Valid album information is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.getCreatorAlbum(username, albumId);

      const data = getResponseData(response);

      if (!data || typeof data !== "object" || Array.isArray(data)) {
        throw new Error("Invalid album response.");
      }

      setAlbum(data);

      return data;
    } catch (requestError) {
      return handleError(
        "getCreatorAlbum",
        requestError,
        "Failed to load album.",
      );
    } finally {
      stopLoading();
    }
  };

  const getCreatorAlbumByTag = async (albumTag) => {
    if (!username || !String(albumTag || "").trim()) {
      setError("Valid album tag is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.getCreatorAlbumByTag(username, albumTag);

      const data = getResponseData(response);

      if (!data || typeof data !== "object" || Array.isArray(data)) {
        throw new Error("Invalid album response.");
      }

      setAlbum(data);

      return data;
    } catch (requestError) {
      return handleError(
        "getCreatorAlbumByTag",
        requestError,
        "Failed to load album.",
      );
    } finally {
      stopLoading();
    }
  };

  const getCreatorAlbumEditData = async (albumId) => {
    if (!username || !isPositiveInteger(albumId)) {
      setError("Valid album information is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.getCreatorAlbumEditData(username, albumId);

      const data = getResponseData(response);
      const result = data?.data || data;

      const currentAlbum = result?.album;
      const groups = result?.groups;

      if (!currentAlbum || typeof currentAlbum !== "object") {
        throw new Error("Invalid album edit response.");
      }

      const groupList = Array.isArray(groups) ? groups : [];

      const sharedGroupIds = normalizeGroupIds(
        groupList
          .filter(
            (group) =>
              group.isShared === true ||
              group.isShared === 1 ||
              group.isShared === "1" ||
              group.is_shared === true ||
              group.is_shared === 1 ||
              group.is_shared === "1",
          )
          .map((group) => group.groupId ?? group.group_id),
      );

      setAlbum(currentAlbum);
      setAlbumDetails(currentAlbum);
      setAvailableGroups(groupList);
      setSelectedGroupIds(sharedGroupIds);

      return {
        album: currentAlbum,
        groups: groupList,
      };
    } catch (requestError) {
      return handleError(
        "getCreatorAlbumEditData",
        requestError,
        "Failed to load album edit data.",
      );
    } finally {
      stopLoading();
    }
  };

  const getCreatorAvailableGroups = async () => {
    if (!username) {
      setError("Creator username is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.getCreatorAvailableGroups(username);

      const data = getResponseData(response);
      const result = data?.data || data;

      const groups = Array.isArray(result)
        ? result
        : Array.isArray(result?.groups)
          ? result.groups
          : [];

      setAvailableGroups(groups);

      return groups;
    } catch (requestError) {
      return handleError(
        "getCreatorAvailableGroups",
        requestError,
        "Failed to load groups.",
      );
    } finally {
      stopLoading();
    }
  };

  const createCreatorAlbum = async (data) => {
    if (
      !username ||
      !data?.title?.trim() ||
      !Array.isArray(data?.images) ||
      !data.images.length
    ) {
      setError("Title and at least one photo are required.");
      return false;
    }

    const visibility = normalizeVisibility(data.visibility);

    if (!ALLOWED_VISIBILITY.includes(visibility)) {
      setError("Invalid album visibility.");
      return false;
    }

    const sharedGroupIds = normalizeGroupIds(data.sharedGroupIds);

    if (visibility === "group" && !sharedGroupIds.length) {
      setError("Select at least one group.");
      return false;
    }

    if (
      data.images.length > 100 ||
      data.images.some(
        (image) =>
          !image?.imageKey ||
          !image?.imageTag ||
          !isPositiveInteger(image?.displayOrder),
      )
    ) {
      setError("Invalid album photos.");
      return false;
    }

    const payload = {
      ...data,
      title: data.title.trim(),
      caption: typeof data.caption === "string" ? data.caption.trim() : "",
      visibility,
      sharedGroupIds: visibility === "group" ? sharedGroupIds : [],
    };

    startLoading();
    setError("");

    try {
      const response = await service.createCreatorAlbum(username, payload);

      const result = getResponseData(response);

      if (!isSuccessful(result)) {
        setError(result?.message || "Failed to create album.");
        return false;
      }

      await refreshAlbumsAfterMutation();

      return result;
    } catch (requestError) {
      return handleError(
        "createCreatorAlbum",
        requestError,
        "Failed to create album.",
      );
    } finally {
      stopLoading();
    }
  };

  const updateCreatorAlbum = async (data) => {
    const albumId = Number(data?.albumId);

    if (!username || !isPositiveInteger(albumId) || !data?.title?.trim()) {
      setError("Valid album ID and title are required.");
      return false;
    }

    const visibility = normalizeVisibility(data.visibility);

    if (!ALLOWED_VISIBILITY.includes(visibility)) {
      setError("Invalid album visibility.");
      return false;
    }

    const payload = {
      title: data.title.trim(),
      caption: typeof data.caption === "string" ? data.caption.trim() : "",
      visibility,
    };

    if (Array.isArray(data.sharedGroupIds)) {
      const groupIds = normalizeGroupIds(data.sharedGroupIds);

      if (visibility === "group" && !groupIds.length) {
        setError("Select at least one group.");
        return false;
      }

      payload.sharedGroupIds = visibility === "group" ? groupIds : [];
    }

    startLoading();
    setError("");

    try {
      const response = await service.updateCreatorAlbum(
        username,
        albumId,
        payload,
      );

      const result = getResponseData(response);

      if (!isSuccessful(result)) {
        setError(result?.message || "Failed to update album.");
        return false;
      }

      const updatedAlbum = result?.data ||
        result?.album || {
          albumId,
          ...payload,
        };

      setAlbum((current) =>
        current && Number(current.albumId) === albumId
          ? { ...current, ...updatedAlbum }
          : current,
      );

      setAlbumDetails((current) =>
        current && Number(current.albumId) === albumId
          ? { ...current, ...updatedAlbum }
          : current,
      );

      setAlbums((current) =>
        current.map((item) =>
          Number(item.albumId) === albumId
            ? { ...item, ...updatedAlbum }
            : item,
        ),
      );

      await refreshAlbumsAfterMutation();

      return result;
    } catch (requestError) {
      return handleError(
        "updateCreatorAlbum",
        requestError,
        "Failed to update album.",
      );
    } finally {
      stopLoading();
    }
  };

  const deleteCreatorAlbum = async (albumId) => {
    if (!username || !isPositiveInteger(albumId)) {
      setError("Valid album information is required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.deleteCreatorAlbum(username, albumId);

      const result = getResponseData(response);

      if (!isSuccessful(result)) {
        setError(result?.message || "Failed to delete album.");
        return false;
      }

      setAlbums((current) =>
        current.filter((item) => Number(item.albumId) !== Number(albumId)),
      );

      setAlbum((current) =>
        Number(current?.albumId) === Number(albumId) ? null : current,
      );

      setAlbumDetails((current) =>
        Number(current?.albumId) === Number(albumId) ? null : current,
      );

      if (result?.cleanupPending) {
        setError("Album deleted, but some S3 files require cleanup.");
      }

      return result;
    } catch (requestError) {
      return handleError(
        "deleteCreatorAlbum",
        requestError,
        "Failed to delete album.",
      );
    } finally {
      stopLoading();
    }
  };

  const deleteCreatorAlbumImage = async (albumId, imageId) => {
    if (
      !username ||
      !isPositiveInteger(albumId) ||
      !isPositiveInteger(imageId)
    ) {
      setError("Valid album and photo IDs are required.");
      return false;
    }

    startLoading();
    setError("");

    try {
      const response = await service.deleteCreatorAlbumImage(
        username,
        albumId,
        imageId,
      );

      const result = getResponseData(response);

      if (!isSuccessful(result)) {
        setError(result?.message || "Failed to delete photo.");
        return false;
      }

      setAlbum((current) => {
        if (!current || Number(current.albumId) !== Number(albumId)) {
          return current;
        }

        return {
          ...current,
          images: (current.images || []).filter(
            (image) => Number(image.imageId) !== Number(imageId),
          ),
        };
      });

      setAlbums((current) =>
        current.map((item) =>
          Number(item.albumId) === Number(albumId)
            ? {
                ...item,
                photoCount: Math.max(0, Number(item.photoCount || 0) - 1),
              }
            : item,
        ),
      );

      if (result?.cleanupPending) {
        setError("Photo deleted, but its S3 file requires cleanup.");
      }

      return result;
    } catch (requestError) {
      return handleError(
        "deleteCreatorAlbumImage",
        requestError,
        "Failed to delete photo.",
      );
    } finally {
      stopLoading();
    }
  };

  const resetCreatorAlbum = () => {
    setAlbum(null);
    setAlbumDetails(null);
    setAvailableGroups([]);
    setSelectedGroupIds([]);
    setError("");
  };

  return {
    albums,
    album,
    albumDetails,
    availableGroups,
    selectedGroupIds,
    loading,
    error,

    setAlbums,
    setAlbum,
    setAlbumDetails,
    setAvailableGroups,
    setSelectedGroupIds,
    setError,

    getCreatorAlbums,
    getCreatorAlbum,
    getCreatorAlbumByTag,
    getCreatorAlbumEditData,
    getCreatorAvailableGroups,

    createCreatorAlbum,
    updateCreatorAlbum,
    deleteCreatorAlbum,
    deleteCreatorAlbumImage,

    resetCreatorAlbum,
  };
};

export default useCreatorAlbums;
